/******/ (() => { // webpackBootstrap
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    // DOM elements
    this.input = this.querySelector('[data-predictive-search-input]');
    this.submitButton = this.querySelector('[data-predictive-search-submit]');
    this.clearButton = this.querySelector('[data-predictive-search-clear]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.emptyStateContainer = this.querySelector('[data-predictive-search-empty-state]');
    this.boxShadowElement = this.closest('.popup-shadow');

    // Initial state
    this.isPredictive = this.getAttribute('is-predictive') === 'true';
    this.searchQuery = this.input.value;
    this.searchChangeTimer = null;
    this.rowObserver = null;
  
    // Bind event handlers
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleClearButtonVisibility = this.handleClearButtonVisibility.bind(this);
    this.handleNavButtonClick = this.handleNavButtonClick.bind(this);
    this.resetState = this.resetState.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-predictive-search'});

    // Add event listeners
    this.input.addEventListener('input', this.handleInputChange);
    this.input.addEventListener('keydown', this.handleInputKeyDown);
    this.submitButton.addEventListener('click', this.handleSubmit);
    this.clearButton.addEventListener('click', this.handleClear);
  }

  handleInputChange() {
    this.handleClearButtonVisibility();

    if (!this.isPredictive) return;

    if (this.input.value !== '') {
      this.searchQuery = this.input.value;

      if (this.searchChangeTimer) {
        window.clearTimeout(this.searchChangeTimer);
      }

      this.searchChangeTimer = window.setTimeout(() => {
        this.performSearch(this.searchQuery);
        this.searchChangeTimer = null;
      }, 250);
    }
    else {
      this.resetState();
    }
  }

  handleClearButtonVisibility() {
    if (this.input.value === '') {
      this.clearButton.classList.add('fade-out');
      setTimeout(() => {
        this.clearButton.classList.add('hide');
        this.clearButton.classList.remove('fade-out');
      }, 250);
    }
    else {
      this.clearButton.classList.remove('hide');
    }
  }

  handleInputKeyDown(event) {
    if (event.key === 'Enter') {
      this.handleSubmit();
    }
  }

  handleSubmit() {
    window.location.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(this.input.value)}*`;
  }

  handleClear() {
    this.input.value = '';
    this.searchQuery = '';
    this.handleClearButtonVisibility();
    this.input.focus();
    this.resetState();
  }

  async performSearch(query) {
    const root = window.Shopify.routes.root;
    const encodedQuery = encodeURIComponent(query);

    // 1) ORIGINAL request (unchanged): suggestions, collections, pages and articles for the tabs
    const suggestUrl = `${root}search/suggest?q=${encodedQuery}&section_id=predictive-search-results&resources[limit]=10&resources[limit_scope]=each&resources[type]=query,product,collection,page,article`;

    // 2) NEW request: up to 30 products from the normal search (the 6-per-row scrollable panel)
    const productsUrl = `${root}search?q=${encodedQuery}&type=product&options[prefix]=last&section_id=predictive-search-results`;

    try {
      const [suggestResponse, productsResponse] = await Promise.all([
        fetch(suggestUrl),
        fetch(productsUrl).catch(() => null)
      ]);
      const suggestText = await suggestResponse.text();
      const productsText = productsResponse && productsResponse.ok ? await productsResponse.text() : '';

      if (suggestResponse.status === 417) {
        return;
      }

      if (this.input.value === '') return;

      if (this.emptyStateContainer) {
        this.emptyStateContainer.setAttribute('hidden', '');
      }

      const parser = new DOMParser();
      const suggestDoc = parser.parseFromString(suggestText, 'text/html');
      let content = suggestDoc.querySelector('.shopify-section');

      // Replace the old 4-product panel with the new 30-product scrollable panel
      if (productsText) {
        const productsDoc = parser.parseFromString(productsText, 'text/html');
        const newProductsPanel = productsDoc.querySelector('.shopify-section [data-grid-id="products"]');

        if (newProductsPanel) {
          const oldProductsPanel = content ? content.querySelector('[data-grid-id="products"]') : null;

          if (oldProductsPanel) {
            oldProductsPanel.replaceWith(newProductsPanel);
          }
          else if (!content || !content.querySelector('[data-grid-id]')) {
            // The original request found nothing, but the new one found products: show only the products HTML
            content = productsDoc.querySelector('.shopify-section');
          }
        }
      }

      if (content) {
        this.renderSearchResults(content.innerHTML);
      }
    } catch (error) {
      console.error(`Failed to fetch search results:`, error);
    }
  }

  renderSearchResults(html) {
    this.resultsContainer.innerHTML = html;

    // Remove any previously tracked event listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
      });
    }

    // Track elements with event listeners for later cleanup
    this.boundNavButtons = [];

    // Add nav button event listeners
    const navButtons = this.resultsContainer.querySelector('[data-predictive-search-nav-buttons]');
    if (navButtons) {
      navButtons.addEventListener('click', this.handleNavButtonClick);
      this.boundNavButtons.push(navButtons);
    }

    // Set the row height of the 30-product scrollable panel to match the cards
    this.fitProductRows();
  }

  // One row height = the tallest card, so every card (image, title, price) is fully visible
  fitProductRows() {
    if (this.rowObserver) {
      this.rowObserver.disconnect();
      this.rowObserver = null;
    }

    const scroller = this.resultsContainer.querySelector('.ps30__scroller');
    const grid = scroller ? scroller.querySelector('.ps30__grid') : null;
    if (!scroller || !grid) return;

    const fit = () => {
      let tallest = 0;
      grid.querySelectorAll('.ps30__item').forEach((item) => {
        tallest = Math.max(tallest, item.offsetHeight);
      });
      if (tallest > 0) {
        const value = `${tallest}px`;
        // Only write when the value changed, to avoid needless re-layouts
        if (scroller.style.getPropertyValue('--ps-row-height') !== value) {
          scroller.style.setProperty('--ps-row-height', value);
        }
      }
    };

    fit();

    // Measure again when images finish loading
    grid.addEventListener('load', fit, true);

    // Measure again when the popup or window width changes
    if ('ResizeObserver' in window) {
      this.rowObserver = new ResizeObserver(fit);
      this.rowObserver.observe(scroller);
    }
  }

  handleNavButtonClick(e) {
    e.stopPropagation();
    const navButton = e.target;
    const resultId = navButton.getAttribute('data-nav-id');
    if (!resultId) return;

    const results = this.resultsContainer.querySelector(`[data-grid-id="${resultId}"]`);
    if (!results) return;

    const previousActiveButton = this.resultsContainer.querySelector(`[data-nav-id].active`);
    const previousActiveResult = this.resultsContainer.querySelector(`[data-grid-id]:not([hidden])`);

    // Set active nav button
    if (previousActiveButton) {
      previousActiveButton.setAttribute('aria-selected', 'false');
      previousActiveButton.classList.remove('active');
    }
    navButton.setAttribute('aria-selected', 'true');
    navButton.classList.add('active');

    // Set active results
    const heightContainer = this.resultsContainer.querySelector('[data-predictive-search-main-results]');
    const beforeHeight = heightContainer.clientHeight;

    if (previousActiveResult) {
      previousActiveResult.setAttribute('hidden', '');
    }
  
    results.removeAttribute('hidden');

    // Transition the height change
    heightContainer.animate([
      { height: `${beforeHeight}px` },
      { height: `${heightContainer.clientHeight}px` }
    ], { duration: 500, easing: 'cubic-bezier(0.85, 0, 0.15, 1)' });
  }

  resetState() {
    if (this.searchChangeTimer) {
      window.clearTimeout(this.searchChangeTimer);
    }

    if (this.emptyStateContainer) {
      this.emptyStateContainer.removeAttribute('hidden');
      this.renderSearchResults('');
      return;
    }

    this.resultsContainer.classList.add('fade-out');
    if (this.boxShadowElement) {
      this.boxShadowElement.classList.remove('popup-shadow');
    }

    setTimeout(() => {
      this.renderSearchResults('');
      this.resultsContainer.classList.remove('fade-out');
      if (this.boxShadowElement) {
        this.boxShadowElement.classList.add('popup-shadow');
      }
    }, 300);
  }

  disconnectedCallback() {
    // Remove event listeners
    this.input.removeEventListener('input', this.handleInputChange);
    this.input.removeEventListener('keydown', this.handleInputKeyDown);
    this.submitButton.removeEventListener('click', this.handleSubmit);
    this.clearButton.removeEventListener('click', this.handleClear);

    // Clean up dynamic listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
      });
    }

    if (this.rowObserver) {
      this.rowObserver.disconnect();
      this.rowObserver = null;
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

/******/ })()
;
